import React, { useState, useEffect, useCallback, useRef } from "react";
import { useStore, StoreTypes } from "context";
import styles from "./index.module.scss";
import classnames from "classnames";
import { Script } from "components/ExtendedContent/ExtendedContentContainer/layout";
import { scriptLoader } from "util/scriptLoader";
import * as types from "constants/actionTypes";
import { parseChineseOnly } from "util/zhuyin";
import {
  ScrollPositionType,
  ScrollLength,
  ScrollCoordinate,
} from "constants/scrollPositionType";
import { scrollPositionButtonBehavior } from "../ExtendedContent/ExtendedContentScrollPositionButton";
import { splitSynopsisIntoPages_108_2 } from "./splitForTopic108_2";
import { splitSynopsisIntoPages_PlainText } from "./splitForPlainText";
import { splitSynopsisIntoPages_Simple108_2 } from "./splitForSimple108_2";
import { LayoutType } from "components/ExtendedContent/ExtendedContentContainer/layout";
import { EventBus, EventBusType } from "events/EventBus";
import { CanvasEvent } from "events/EventTypes";
import { SplitType } from "constants/splitType";

const pageSize = 7;


const SynopsisWindow = ({ synopsisInfo, extendedResourceStyle }) => {
  const [{ shouldSplited, splitType }] = useStore(StoreTypes.extendedContent);
  const { userAgent } = navigator;
  const listRef = useRef();
  const containerRef = useRef();
  const contentRef = useRef();
  const { list } = synopsisInfo;
  const [{ synopsisList, synopsisTab, fontSize, currentPage, totalPages }, extendedContentDispatch] =
    useStore(StoreTypes.extendedContent);
  const [{ bookId, books }] = useStore(StoreTypes.books);
  const [isChineseSecondary, setIsChineseSecondary] = useState(false);
  const [isChineseElementary, setIsChineseElementary] = useState(false);
  const book = books.find((info) => info.bookId === bookId);
  const elementaryHighGradeCheck =
    book.display_name.includes("國小國語5") ||
    book.display_name.includes("國小國語6") ||
    book.display_name.includes("國中國文"); //新增國中國文行距要縮小的需求

  const [{ scrollPosition }] = useStore(StoreTypes.extendedContent);
  const [currentScrollTop, setCurrentScrollTop] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [pagedHtml, setPagedHtml] = useState(null);
  const currentScriptRef = useRef(null);
  const prevPageRef = useRef(currentPage);
  const prevTabRef = useRef(synopsisTab);

  useEffect(() => {
    if (synopsisList[synopsisTab] && Script[extendedResourceStyle]) {
      const scriptSrc = Script[extendedResourceStyle];
      
      // 如果已經有載入的 script，先移除舊的
      if (currentScriptRef.current) {
        const oldScript = currentScriptRef.current;
        if (oldScript && oldScript.parentNode) {
          oldScript.parentNode.removeChild(oldScript);
        }
      }
      
      // 載入新的 script
      requestAnimationFrame(() => {
        const script = scriptLoader(scriptSrc);
        currentScriptRef.current = script;
      })

      
      // 清理函數：在 state 變動或組件卸載時移除 script
      return () => {
        if (currentScriptRef.current) {
          const scriptToRemove = currentScriptRef.current;
          if (scriptToRemove && scriptToRemove.parentNode) {
            scriptToRemove.parentNode.removeChild(scriptToRemove);
          }
          currentScriptRef.current = null;
        }
      };
    }
  }, [extendedResourceStyle, synopsisList, synopsisTab]);

  // 重置內容縮放到預設大小
  const resetContentScale = useCallback(() => {
    if (!containerRef.current) return;
    const content = containerRef.current;
    content.style.fontSize = '';
    content.style.transform = 'none';
    content.style.transformOrigin = '';
    content.style.height = '';
    content.style.width = '100%';
  }, []);

  // 分頁處理
  useEffect(() => {
    if (!synopsisList[synopsisTab] || !containerRef.current) return;

    if (splitType === SplitType.MANUAL_SPLIT) {
      setPagedHtml(synopsisList[synopsisTab]['1']);

      const totalPages = Object.keys(synopsisList[synopsisTab]).length;
      // 更新 store 中的總頁數
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages },
      });
      return;
    }

    const html = elementaryHighGradeCheck
      ? parseChineseOnly(synopsisList[synopsisTab].html, "pirls")
      : synopsisList[synopsisTab].html;

    if (!html) return;

    if (!shouldSplited || (shouldSplited && splitType === SplitType.NO_SPLIT)) {
      setPagedHtml(html);
      return
    }

    resetContentScale();
    
    // 使用 requestAnimationFrame 確保 DOM 更新完成後再進行分頁
      requestAnimationFrame(() => {
        if (extendedResourceStyle === LayoutType.PlainText) {
          const computedStyle = window.getComputedStyle(containerRef.current);
          const lineHeight = parseFloat(computedStyle.lineHeight) || 
                            parseFloat(computedStyle.fontSize) * 1.5;
          
          const { htmlString, totalPages: calculatedTotalPages } = splitSynopsisIntoPages_PlainText(
            html, 
            lineHeight, 
            pageSize,
            containerRef.current
          );
          
          setPagedHtml(htmlString);
          
          extendedContentDispatch({
            type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
            payload: { totalPages: calculatedTotalPages },
          });
          return;
        }

        if (extendedResourceStyle === LayoutType.Topic_108_2) {
        const computedStyle = window.getComputedStyle(containerRef.current);
        const lineHeight = parseFloat(computedStyle.lineHeight) || 
                          parseFloat(computedStyle.fontSize) * 1.5;
        
        const { htmlString, totalPages: calculatedTotalPages } = splitSynopsisIntoPages_108_2(
          html, 
          lineHeight, 
          pageSize,
          containerRef.current
        );
        
          setPagedHtml(htmlString);
          
          extendedContentDispatch({
            type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
            payload: { totalPages: calculatedTotalPages },
          });
          return;
        }
        
        if (extendedResourceStyle === LayoutType.Simple_108_2) {
          const computedStyle = window.getComputedStyle(containerRef.current);
          const lineHeight = parseFloat(computedStyle.lineHeight) || 
                            parseFloat(computedStyle.fontSize) * 1.5;
          
          const { htmlString, totalPages: calculatedTotalPages } = splitSynopsisIntoPages_Simple108_2(
            html, 
            lineHeight, 
            pageSize,
            containerRef.current
          );
          
          setPagedHtml(htmlString);
          
          extendedContentDispatch({
            type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
            payload: { totalPages: calculatedTotalPages },
          });
          return;
        }
      });
  }, [synopsisList, synopsisTab, elementaryHighGradeCheck, extendedContentDispatch, resetContentScale, shouldSplited, extendedResourceStyle]);

  // 更新顯示的頁面
  const updateDisplayPage = useCallback((pageIndex) => {
    if (!contentRef.current) return;
    
    const pageElements = contentRef.current.querySelectorAll('.synopsis-page');
    pageElements.forEach((pageEl, index) => {
      if (index === pageIndex) {
        pageEl.style.visibility = 'visible';
        pageEl.style.position = 'relative';
      } else {
        pageEl.style.visibility = 'hidden';
        pageEl.style.position = 'absolute';
      }
    });
  }, []);

  // 響應 store 中的 currentPage 變化
  useEffect(() => {
    if (pagedHtml && contentRef.current && totalPages > 0 && currentPage >= 0) {
      updateDisplayPage(currentPage);
    }
  }, [pagedHtml, currentPage, totalPages, updateDisplayPage]);

  // 當 currentPage 變化時，保存舊頁面的畫記並載入新頁面的畫記
  useEffect(() => {
    if (!shouldSplited) return;
    if (pagedHtml && totalPages > 0 && currentPage >= 0 && synopsisTab >= 0) {
      // 如果 tab 改變了，更新 tab ref（保存已在 onTabClickHandler 中處理）
      if (prevTabRef.current !== synopsisTab) {
        prevTabRef.current = synopsisTab;
      }
      
      // 如果頁面真的改變了，保存舊頁面的畫記
      if (prevPageRef.current !== currentPage && prevPageRef.current >= 0) {
        EventBus.emit({
          eventBusType: EventBusType.ExtendedContent,
          event: CanvasEvent.SplitedPageSaveCanvasEvent,
          payload: { 
            currentSplitedPageIndex: prevPageRef.current,
            tabIndex: synopsisTab
          }
        });
      }
      
      // 載入新頁面的畫記
      EventBus.emit({
        eventBusType: EventBusType.ExtendedContent,
        event: CanvasEvent.SplitedPageImportJSONEvent,
        payload: { 
          currentSplitedPageIndex: currentPage,
          tabIndex: synopsisTab
        }
      });
      
      // 更新 ref
      prevPageRef.current = currentPage;
    }
  }, [currentPage, pagedHtml, totalPages, synopsisTab, shouldSplited]);

  // 調整內容縮放，使 8 行內容也能顯示在視窗中
  const adjustContentScale = useCallback(() => {
    if (!listRef.current || !contentRef.current) return;
    const container = listRef.current;
    const content = contentRef.current;

    // 重置所有樣式，確保測量準確
    content.style.fontSize = '';
    content.style.transform = 'none';
    content.style.transformOrigin = '';
    content.style.height = '';
    content.style.width = '100%';

    // 強制重排一次
    void container.offsetHeight;

    // 計算容器和內容高度
    const containerHeight = container.clientHeight;
    let contentHeight = content.scrollHeight;
    // 如果內容高度超過容器高度，使用二分搜索找到最佳字體大小
    if (contentHeight > containerHeight && contentHeight > 0) {
      // 取得原始字體大小
      const computedStyle = window.getComputedStyle(content);
      const originalFontSize = parseFloat(computedStyle.fontSize);
      
      // 二分搜索參數
      let minFontSize = originalFontSize * 0.3; // 最小 30%
      let maxFontSize = originalFontSize; // 最大 100%
      const tolerance = 5; // 容許 5px 誤差
      const maxIterations = 20;
      
      let bestFontSize = minFontSize;
      let iteration = 0;
      
      while (maxFontSize - minFontSize > 0.5 && iteration < maxIterations) {
        const midFontSize = (minFontSize + maxFontSize) / 2;
        
        // 應用測試字體大小
        content.style.fontSize = `${midFontSize}px`;
        
        // 強制重排
        void content.offsetHeight;
        
        // 測量調整後的實際高度
        const actualHeight = content.scrollHeight;
        
        if (actualHeight <= containerHeight + tolerance) {
          // 高度合適，記錄這個字體大小，嘗試更大的值
          bestFontSize = midFontSize;
          minFontSize = midFontSize;
        } else {
          // 高度太大，需要更小的字體
          maxFontSize = midFontSize;
        }
        
        iteration++;
      }
      
      // 應用最佳字體大小
      content.style.fontSize = `${bestFontSize}px`;
      
    } else {
      // 內容未超過容器，保持預設字體大小
      content.style.fontSize = '';
    }
  }, []);

  // 當頁面切換、內容變化或字體大小改變時調整縮放
  useEffect(() => {
    if (pagedHtml && containerRef.current && shouldSplited) {
        requestAnimationFrame(() => {
          adjustContentScale();
        });
    }
  }, [pagedHtml, currentPage, fontSize, adjustContentScale, shouldSplited, synopsisTab]);

  useEffect(() => {
    let isChineseSecondary = false,
      isChineseElementary = false;
    synopsisList.forEach((item) => {

      if (splitType === SplitType.MANUAL_SPLIT) {
        if (item[synopsisTab] && item[synopsisTab]['1'].includes("CHINESE SECONDARY")) {
          isChineseSecondary = true;
        } else if (item[synopsisTab] && item[synopsisTab]['1'].includes("CHINESE ELEMENTARY")) {
          isChineseElementary = true;
        }
      } else {
        if (item.html.includes("CHINESE SECONDARY")) {
          //  判斷國中國文
          isChineseSecondary = true;
        } else if (item.html.includes("CHINESE ELEMENTARY")) {
          //  判斷國小國文
          isChineseElementary = true;
        }
      }
      
    });
    setIsChineseSecondary(isChineseSecondary);
    setIsChineseElementary(isChineseElementary);
  }, [synopsisList, synopsisTab, splitType]);

  const onTabClickHandler = useCallback(
    (index) => {
      // 如果點擊的是同一個 tab，不需要處理
      if (index === synopsisTab) return;
      
      // 保存舊 tab 當前頁面的畫記
      if (pagedHtml && totalPages > 0 && currentPage >= 0 && synopsisTab >= 0 && shouldSplited) {
        EventBus.emit({
          eventBusType: EventBusType.ExtendedContent,
          event: CanvasEvent.SplitedPageSaveCanvasEvent,
          payload: { 
            currentSplitedPageIndex: currentPage,
            tabIndex: synopsisTab
          }
        });
      }
      
      // 切換 tab 時先重置 scale 到預設大小
      resetContentScale();
      
      // 切換 tab
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_SYNOPSIS_TAB,
        payload: {
          synopsisTab: index,
        },
      });
      
      if (shouldSplited) {
        // 重置到第一頁（這會觸發 currentPage 變化的 useEffect，自動載入新 tab 的畫記）
        extendedContentDispatch({
          type: types.SET_EXTENDED_CONTENT_CURRENT_PAGE,
          payload: { currentPage: 0 },
        });
      }
    },
    [extendedContentDispatch, synopsisTab, currentPage, pagedHtml, totalPages, shouldSplited, resetContentScale]
  );

  useEffect(() => {
    if (splitType === SplitType.MANUAL_SPLIT) {
      const page = (currentPage + 1).toString();
      setPagedHtml(synopsisList[synopsisTab][page]);
      return;
    }
  }, [synopsisList, synopsisTab, splitType, currentPage]);

  useEffect(() => {
    listRef.current && listRef.current.scrollTo(listRef.current.scrollWidth, 0);
  }, [synopsisTab]);

  useEffect(() => {
    // 小視窗內容上下頁分段
    // console.log("---C類小視窗---SynopsisWindow", listRef.current);
    switch (scrollPosition) {
      case ScrollPositionType.Next: {
        setLastScrollTop(listRef.current.scrollTop);
        listRef.current.scrollTop +=
          listRef.current.offsetHeight * ScrollLength;
        setCurrentScrollTop(listRef.current.scrollTop);
        break;
      }
      case ScrollPositionType.Prev: {
        setLastScrollTop(listRef.current.scrollTop);
        listRef.current.scrollTop -=
          listRef.current.offsetHeight * ScrollLength;
        setCurrentScrollTop(listRef.current.scrollTop);
        break;
      }
      default: {
        scrollPositionButtonBehavior({
          currentScroll: currentScrollTop,
          lastScroll: lastScrollTop,
          coordinate: ScrollCoordinate.Y,
        });
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition]);

  // 文字排版，如稿紙徹底對齊文字和標點
  useEffect(() => {
    setTimeout(() => {
      const isChineseOnly = listRef.current.querySelector(".chineseOnly");
      if (isChineseOnly) return;
      const ruby = listRef.current.querySelector(":not(.oval) > .az");
      const paragraphIndent = listRef.current.querySelector(".indent");
      const rubyHeight = ruby ? getComputedStyle(ruby).height : null;
      const punctuation = listRef.current.querySelectorAll(
        ":not(.circle) > sign"
      );
      const listBegin = listRef.current.querySelectorAll(".begin");
      const circleListBegin = listRef.current.querySelectorAll(".circle");
      punctuation &&
        punctuation.forEach((element) => {
          element.style.height = rubyHeight;
          // element.style.display = 'inline-flex';
          // element.style.textIndent = 'initial';
          // element.style.justifyContent = 'center';
        });
      listBegin &&
        listBegin.forEach((element) => {
          if (element.childElementCount === 0) return;
          // element.style.display = 'inline-block';
          element.style.height = rubyHeight;
          element.style.width = rubyHeight;
        });
      circleListBegin &&
        circleListBegin.forEach((element) => {
          element.style.height = rubyHeight;
          element.style.width = rubyHeight;
        });
      if (paragraphIndent)
        paragraphIndent.style.textIndent = `${
          2 * rubyHeight.replace("px", "")
        }px`;
    }, 100);
  }, [fontSize, synopsisList, synopsisTab, listRef]);

  return (
    <div
      className={classnames(
        styles.synopsisWindow,
        {
          [styles.wordSection]: extendedResourceStyle === "WordSection",
          [styles.ChineseSecondary]: isChineseSecondary,
          [styles.ChineseElementary]: isChineseElementary,
        },
        fontSize
      )}
    >
      <div
        className={classnames(styles.tab_box, {
          [styles.mobile]:
            /Android/i.test(userAgent) || /iPhone|iPad|iPod/i.test(userAgent),
        })}
      >
        {list &&
          list.map((obj, i) => (
            <div
              className={classnames(styles.tab, {
                [styles.active]: synopsisTab === i,
              })}
              key={i}
              onClick={() => onTabClickHandler(i)}
            >
              {obj.name}
            </div>
          ))}
      </div>
      <div ref={listRef} className={classnames(styles.list, { [styles.noScroll]: shouldSplited })}>
        <div ref={containerRef} className={styles.contentContainer}>
          {pagedHtml && (
            <div
              ref={contentRef}
              dangerouslySetInnerHTML={{
                __html: pagedHtml,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};



export default SynopsisWindow;
